<template>
  <div class="card">
    <div class="card-body">
      <h4 class="card-title">
        Dataset Difference
        <span>
          {{ "(" }}Revision {{ previousRevisionNumber }} - Revision
          {{ latestRevisionNumber }}{{ ")" }}</span
        >
      </h4>

      <div class="grid-table-container">
        <div class="ec-sort dropdown-container">
          <div
            class="col-right mr-2 d-inline"
            role="group"
            aria-label="Sort Dropdown"
          >
            <div class="btn-group ec-sort-dropdown left-dropdown" role="group">
              <button
                class="colorPrimarySort value-display list-group-item col w-100 d-flex flex-row justify-content-md-between p-0 align-items-center"
                type="button"
                @click="toggleDropdown"
              >
                <div class="pl-2 h-100 d-flex align-items-center">
                  {{ selectedLabel }}
                </div>
                <div class="ecl-select__icon ecl-select__icon-Sort">
                  <svg
                    class="ecl-icon ecl-icon--s ecl-icon--rotate-180 ecl-select__icon-shape"
                    focusable="false"
                    aria-hidden="true"
                  >
                    <use
                      xlink:href="../../assets/img/ecl/icons.svg#corner-arrow"
                    ></use>
                  </svg>
                </div>
              </button>
              <ul
                class="dropdown-menu ec-ds-dropdown-items"
                :class="{ show: dropdownVisible }"
              >
                <li v-for="option in dropdownList" :key="option.value">
                  <button
                    class="dropdown-item"
                    @click="onSelectionChange(option)"
                  >
                    {{ option.label }}
                  </button>
                </li>
              </ul>
            </div>

            <div class="btn-group ec-sort-dropdown" role="group">
              <button
                class="colorPrimarySort value-display list-group-item col w-100 d-flex flex-row justify-content-md-between p-0 align-items-center"
                type="button"
                @click="toggleLatestDropdown"
              >
                <div class="pl-2 h-100 d-flex align-items-center">
                  {{ selectedLatestLabel }}
                </div>
                <div class="ecl-select__icon ecl-select__icon-Sort">
                  <svg
                    class="ecl-icon ecl-icon--s ecl-icon--rotate-180 ecl-select__icon-shape"
                    focusable="false"
                    aria-hidden="true"
                  >
                    <use
                      xlink:href="../../assets/img/ecl/icons.svg#corner-arrow"
                    ></use>
                  </svg>
                </div>
              </button>
              <ul
                class="dropdown-menu ec-ds-dropdown-items"
                :class="{ show: dropdownLatestVisible }"
              >
                <li v-for="option in dropdownList" :key="option.value">
                  <button
                    class="dropdown-item"
                    @click="onLatestSelectionChange(option)"
                  >
                    {{ option.label }}
                  </button>
                </li>
              </ul>
            </div>
          </div>
          <button
            class="p-0 ecl-button show-more row text-primary mt-2"
            @click="cmpDatasetDiff($event)"
            type="button"
          >
            <span
              data-v-9aa62744=""
              class="ecl-button__label"
              data-ecl-label="true"
              >Compare Revision</span
            >
          </button>
        </div>
      </div>
      <div v-if="oldVersion && newVersion">
        <CodeDiff
          :old-string="oldVersion"
          :new-string="newVersion"
          output-format="side-by-side"
          language="json"
        >
          <template #stat="{ stat }">
            <span class="diff-stat-added"
              >+{{ stat.additionsNum }} {{ addStats }}</span
            >
            <span class="diff-stat-deleted"
              >-{{ stat.deletionsNum }} {{ subStats }}</span
            >
          </template>
        </CodeDiff>
      </div>
      <div v-else class="cardNotFound">
        <p>No differences found between the selected dataset revisions.</p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref, onMounted, watch, inject } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { getDiff, getDiff2, compareRevisions } from "./diffAPI";
import { useRuntimeEnv } from "@piveau/piveau-hub-ui-modules";
import stringifyJson from "json-stable-stringify";

interface Option {
  value: string;
  label: string;
}
interface SelectedItem {
  label: string;
  value: string;
}
interface Revision {
  modified: string;
  revisionID: string;
  revisionNumber: string;
}
export default defineComponent({
  name: "DatasetDiffDetails",
  setup() {
    const oldVersion = ref("");
    const newVersion = ref("");
    const oldVersionNumber = ref("-");
    const newVersionNumber = ref("-");
    const previousRevisionNumber = ref("-");
    const latestRevisionNumber = ref("-");
    const route = useRoute();
    const env = useRuntimeEnv();
    const { t, te } = useI18n();
    const dropdownVisible = ref(false);
    const dropdownLatestVisible = ref(false);
    const selectedItem = ref<SelectedItem[]>([]);
    const revisionsArray = ref<Revision[]>([]);
    const dropdownList = ref<Option[]>([]);
    const selectedLabel = ref("");
    const selectedLatestLabel = ref("");
    const selectedValue = ref("");
    const selectedLatestValue = ref("");
    const router = useRouter();

    const injected = inject("myObject");

    if (!injected) {
      throw new Error("error");
    }
    watch(injected.apiData, (newValue) => {
      selectedItem.value = newValue;
    });
    watch(injected.revisionData, (newValue) => {
      revisionsArray.value = newValue;
    });

    watch(
      revisionsArray,
      (newData) => {
        const transformDropdown = newData.map((revision) => ({
          label: `Revision ${revision.revisionNumber} - ${
            revision.modified.split("T")[0]
          }`,
          value: revision.revisionID,
        }));

        let latestRevision = {
          label: "Current Revision",
          value: route.params.ds_id?.toString(),
        };
        let latestObject = {
          modified: "T",
          revisionID: route.params.ds_id?.toString(),
          revisionNumber: "Current",
        };
        revisionsArray.value.unshift(latestObject);
        transformDropdown.unshift(latestRevision);
        dropdownList.value = transformDropdown;
      },
      { immediate: true }
    );

    const onSelectionChange = (option: any) => {
      selectedLabel.value = option.label;
      selectedValue.value = option.value;
      dropdownVisible.value = false;
    };
    const onLatestSelectionChange = (option: any) => {
      selectedLatestLabel.value = option.label;
      selectedLatestValue.value = option.value;
      dropdownLatestVisible.value = false;
    };

    const toggleDropdown = () => {
      dropdownVisible.value = !dropdownVisible.value;
    };
    const toggleLatestDropdown = () => {
      dropdownLatestVisible.value = !dropdownLatestVisible.value;
    };

    const addStats = computed(() => {
      return te("message.metadata.datasetRevision.addition")
        ? t("message.metadata.datasetRevision.addition")
        : "Additions";
    });
    const subStats = computed(() => {
      return te("message.metadata.datasetRevision.subtractions")
        ? t("message.metadata.datasetRevision.subtractions")
        : "Subtractions";
    });
    const updateRevision = (e) => {
      e.preventDefault();
      router.push({
        query: {
          ...route.query,
          srcRevision: selectedValue.value,
          targetRevision: selectedLatestValue.value,
        },
      });
    };
    const cmpDatasetDiff = async (e) => {
      updateRevision(e);
    };
    onMounted(async () => {
      const options = {
        baseUrl: `${env.api.hubUrl}`,
        datasetId: route.params.ds_id.toString(),
        revisionId: (route.query.targetRevision
          ? route.query.targetRevision
          : route.params.ds_id
        ).toString(),
      };
      const diffData = await compareRevisions(options);
      const oldData =
        stringifyJson(diffData, {
          space: 2,
        }) || "{}";

      const options2 = {
        baseUrl: `${env.api.hubUrl}`,
        datasetId: route.params.ds_id.toString(),
        revisionId: (route.query.srcRevision
          ? route.query.srcRevision
          : ""
        ).toString(),
      };

      const diffData2 = await compareRevisions(options2);
      const newData =
        stringifyJson(diffData2, {
          space: 2,
        }) || "{}";

      if (oldData && newData) {
        oldVersion.value = newData;
        newVersion.value = oldData;
      }
      const srcRevision = computed(
        () => route.query.srcRevision?.toString() || ""
      );
      const dsId = computed(() => route.params.ds_id?.toString() || "");

      oldVersionNumber.value = srcRevision.value;

      newVersionNumber.value = route.query.targetRevision
        ? route.query.targetRevision?.toString()
        : dsId.value;

      selectedValue.value = srcRevision.value;
      selectedLatestValue.value = route.query.targetRevision
        ? route.query.targetRevision?.toString()
        : dsId.value;

      previousRevisionNumber.value =
        revisionsArray.value.find(
          (option: Revision) => option.revisionID == selectedValue.value
        )?.revisionNumber || "";

      latestRevisionNumber.value =
        revisionsArray.value.find(
          (option: Revision) => option.revisionID == selectedLatestValue.value
        )?.revisionNumber || "";

      watch(
        [dropdownList, oldVersionNumber],
        () => {
          selectedLabel.value =
            dropdownList.value.find(
              (option) => option.value == oldVersionNumber.value
            )?.label || "";
        },
        { immediate: true }
      );

      watch(
        [dropdownList, newVersionNumber],
        () => {
          selectedLatestLabel.value =
            dropdownList.value.find(
              (option) => option.value == newVersionNumber.value
            )?.label || "";
        },
        { immediate: true }
      );
    });

    return {
      oldVersion,
      newVersion,
      addStats,
      subStats,
      oldVersionNumber,
      newVersionNumber,
      toggleDropdown,
      onSelectionChange,
      dropdownList,
      selectedLabel,
      selectedItem,
      dropdownVisible,
      cmpDatasetDiff,
      toggleLatestDropdown,
      selectedLatestLabel,
      dropdownLatestVisible,
      onLatestSelectionChange,
      previousRevisionNumber,
      latestRevisionNumber,
      updateRevision,
    };
  },
});
</script>
<style scoped lang="scss">
.cardNotFound {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 0;
  word-wrap: break-word;
  background-color: #fff;
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  margin-top: 30px;
  flex: 1 1 auto;
  min-height: 1px;
  padding: 1.25rem;
}
.cardNotFound p {
  font-size: 22px !important;
  padding-top: 5px !important;
  padding-bottom: 30px !important;
}
.card,
.dataset-info-box {
  background-clip: border-box;
  border: 1px solid rgba(0, 0, 0, 0.125);
  border-radius: 0.25rem;
  flex: 1 1 auto;
  min-height: 1px;
  padding: 0.75rem;
}
.card-title {
  text-align: start;
  margin-bottom: 45px;
}
.revision-container {
  background-color: #f3f6fc;
  border-radius: 4px;
  margin-bottom: 25px !important;
}
.revision-container h6 {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: #212529;
}
p {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
}

.grid-table-container {
  display: flex;
  justify-content: start;
  position: relative;
  align-items: flex-start;
}

.dropdown-container {
  margin-top: 5px !important;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}
.left-dropdown {
  margin-right: 50px;
}
.dropdown-container p {
  font-size: 0.95rem;
}
.ec-sort-dropdown {
  width: 300px;
  height: 48px;
  border-radius: 4px;
}
.colorPrimarySort {
  border-radius: 4px;
}
.ec-ds-dropdown-items {
  width: 300px;
  background-color: #fff !important;
  color: #7f7f7f !important;
}
.ecl-button.show-more {
  background-color: #0e47cb !important;
  border: 1px solid #0e47cb !important;
  color: #fff !important;
  padding: 12px 3px !important;
  font-size: 20px !important;
  border-radius: 5px !important;
  width: 35% !important;
}
.dropdown-item {
  margin-top: 3px;
  margin-bottom: 3px;
}
</style>
